//React imports
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

//Redux imports
import { snoozeNotification } from "../../../redux/features/Notifications";

// Component Import
import PrimaryButton from "../CommonButtons/PrimaryButton";
import SVGComponent from "../SVGComponent";
import CommonCalendar from "../CommonCalendar";
import CommonTimePicker from "../CommonDropdown/SetTime";

//  UTLIS Imports
import {
  GlobalConfiguration,
  IntervalConfiguration,
  TimePickerConfiguration,
} from "../../../utils/configurationProvider";

import ICONS from "../../../assets/Icons";

/**
 * @param {Object} props
 * @param {string} props.headerTitle - Main title text to display at the top of popup
 * @param {string} [props.secondHeaderTitle] - Optional secondary header text below main title
 * @param {OnClickEventHandler} props.btnOneHandleFunction - Handle function when button one is clicked
 * @param {OnClickEventHandler} props.btnTwoHandleFunction - Handle function when button two is clicked
 * @returns {JSX.Element}
 */
const SnoozePopup = ({
  headerTitle,
  secondHeaderTitle,
  btnOneHandleFunction,
  notificationIdProp,
  initialSnoozedUntil,
  onConfirm,
}) => {
  const dispatch = useDispatch();

  // State variables
  const [animate, setAnimate] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [selectedSnoozeOption, setSelectedSnoozeOption] = useState("");
  const [isCustomSnoozeOpen, setIsCustomSnoozeOpen] = useState(false);
  const [timeData, setTimeData] = useState({
    snoozeTime: { hours: "", minutes: "", meridiem: "AM" },
  });
  const [isTimeConfirmed, setIsTimeConfirmed] = useState(false);
  const [calendarCloseSignal, setCalendarCloseSignal] = useState(
    GlobalConfiguration.lengthCheckZero,
  );
  const [dropdown, setDropdown] = useState(true);

  const { notificationId } = useSelector(
    (state) => state?.notificationsSlice || {},
  );

  // Helper function to convert text to sentence case
  const toSentenceCase = (text = "") =>
    text.charAt(GlobalConfiguration?.lengthCheckZero).toUpperCase() +
    text.slice(GlobalConfiguration?.lengthCheckOne).toLowerCase();

  // Determine if a quick snooze option is selected
  const isQuickSnoozeSelected = Boolean(selectedSnoozeOption);

  // Convert the initial snoozed until timestamp to a number for comparison
  const initialSnoozedUntilTs = Number(initialSnoozedUntil);

  // Normalize the initial snoozed until timestamp for comparison
  const normalizedInitialSnoozedUntil = initialSnoozedUntilTs
    ? Math.floor(initialSnoozedUntilTs / 60) * 60
    : null;

  // Check if a valid time has been selected for snoozing
  const hasTimeSelected = Boolean(
    timeData?.snoozeTime?.hours && timeData?.snoozeTime?.minutes,
  );

  // Quick snooze options and placeholder text
  const QUICK_SNOOZE_OPTIONS = ["15 minutes", "1 hour", "4 hours", "12 hours"];
  const placeholder = "Quick snooze";

  // Determine if a custom snooze option is selected based on the presence of a start date and valid time data
  const isCustomSnoozeSelected = Boolean(
    startDate && timeData?.snoozeTime?.hours && timeData?.snoozeTime?.minutes,
  );

  // Helper function to calculate the snoozed until timestamp based on the selected quick snooze option
  const getSnoozedUntilFromOption = () => {
    const now = new Date();

    switch (selectedSnoozeOption) {
      case "15 minutes":
        return new Date(now.getTime() + 15 * 60 * 1000);

      case "1 hour":
        return new Date(now.getTime() + 60 * 60 * 1000);

      case "4 hours":
        return new Date(now.getTime() + 4 * 60 * 60 * 1000);

      case "12 hours":
        return new Date(now.getTime() + 12 * 60 * 60 * 1000);

      default:
        return null;
    }
  };

  // Helper function to get the snoozed until timestamp based on the selected quick snooze option
  const getQuickSnoozeDateTime = () => {
    if (!isQuickSnoozeSelected) return null;
    return getSnoozedUntilFromOption();
  };

  // Handle date change from the calendar component
  const handleDateChange = (date) => {
    setStartDate(Array.isArray(date) ? date[0] : date);
  };

  // Handle time confirmation from the time picker component
  const handleTimeConfirm = () => {
    setIsTimeConfirmed(true);
  };

  // Build the snooze date and time based on the selected start date and time data
  const buildSnoozeDateTime = () => {
    if (!startDate) return null;

    const { hours, minutes, meridiem } = timeData.snoozeTime;

    if (!hours || !minutes) return null;

    let h = parseInt(hours, GlobalConfiguration.panLength);

    if (meridiem === "PM" && h !== TimePickerConfiguration.hour)
      h += TimePickerConfiguration.hour;
    if (meridiem === "AM" && h === TimePickerConfiguration.hour)
      h = GlobalConfiguration.lengthCheckZero;

    const finalDate = new Date(startDate);
    finalDate.setHours(h);
    finalDate.setMinutes(parseInt(minutes, GlobalConfiguration.panLength));
    finalDate.setSeconds(GlobalConfiguration.lengthCheckZero);
    finalDate.setMilliseconds(GlobalConfiguration.lengthCheckZero);

    return finalDate;
  };

  // Build the selected snooze date and time based on user input
  const selectedSnoozeDate = buildSnoozeDateTime();

  // Calculate the selected snoozed until timestamp based on the selected snooze date
  const selectedSnoozedUntil = selectedSnoozeDate
    ? Math.floor(
        selectedSnoozeDate.getTime() / IntervalConfiguration.intervalMedium,
      )
    : null;

  // Determine if the snooze selection has changed compared to the initial value
  const isSnoozeUnchanged =
    Boolean(normalizedInitialSnoozedUntil) &&
    selectedSnoozedUntil !== null &&
    selectedSnoozedUntil === normalizedInitialSnoozedUntil;

  // Handle snooze button click, validate input, and dispatch snooze action
  const handleSnoozeClick = () => {
    let snoozeDate;

    if (isQuickSnoozeSelected) {
      snoozeDate = getQuickSnoozeDateTime();
    } else {
      snoozeDate = buildSnoozeDateTime();

      const { isValid, message } = getSnoozeValidation();

      if (!isValid) {
        return;
      }
    }

    if (!snoozeDate) return;

    const snoozedUntil = String(
      Math.floor(snoozeDate.getTime() / IntervalConfiguration.intervalMedium),
    );

    const idToUse = notificationIdProp || notificationId;

    if (typeof onConfirm === "function") {
      onConfirm(idToUse, snoozedUntil);
      return;
    }

    dispatch(
      snoozeNotification({
        id: idToUse,
        snoozedUntil,
      }),
    );
  };

  // Validate the snooze selection based on the current state and input
  const getSnoozeValidation = () => {
    if (!startDate) {
      return { isValid: false, message: "Please select date and time" };
    }

    if (!isTimeConfirmed) {
      return { isValid: false, message: "" };
    }

    if (!(timeData?.snoozeTime?.hours && timeData?.snoozeTime?.minutes)) {
      return { isValid: false, message: "Please select date and time" };
    }

    const snoozeDate = buildSnoozeDateTime();
    if (!snoozeDate || snoozeDate <= new Date()) {
      return { isValid: false, message: "Future time required" };
    }

    return { isValid: true, message: "" };
  };

  // Validation and state management for snooze functionality
  const snoozeValidation = getSnoozeValidation();

  // Determine if the confirm button should be disabled based on validation and selection state
  const isConfirmDisabled =
    (!isQuickSnoozeSelected && !snoozeValidation?.isValid) || isSnoozeUnchanged;

  // Determine if an invalid snooze time error should be displayed based on the current state
  const showInvalidSnoozeTimeError =
    Boolean(startDate) &&
    Boolean(isTimeConfirmed) &&
    hasTimeSelected &&
    snoozeValidation?.message === "Future time required";

  // Handle changes to the time data from the time picker component
  const handleTimeDataChange = (nextValue) => {
    setIsTimeConfirmed(false);
    setTimeData((prev) =>
      typeof nextValue === "function" ? nextValue(prev) : nextValue,
    );
  };

  // Clear the custom snooze selection and reset related state variables
  const clearCustomSnooze = () => {
    setStartDate(null);
    setTimeData({
      snoozeTime: {
        hours: "",
        minutes: "",
        meridiem: "AM",
      },
    });
    setIsTimeConfirmed(false);
  };

  // Effect to initialize the snooze popup with the initial snoozed until timestamp
  useEffect(() => {
    const initialTs = Number(initialSnoozedUntil);
    if (!initialTs) return;

    const initialDate = new Date(
      initialTs * IntervalConfiguration.intervalMedium,
    );
    const hours24 = initialDate.getHours();
    const hours12 =
      hours24 % TimePickerConfiguration.hour || TimePickerConfiguration.hour;
    const minutes = initialDate.getMinutes();
    const meridiem = hours24 >= TimePickerConfiguration.hour ? "PM" : "AM";

    setStartDate(initialDate);
    setTimeData({
      snoozeTime: {
        hours: String(hours12).padStart(
          GlobalConfiguration.lengthCheckTwo,
          "0",
        ),
        minutes: String(minutes).padStart(
          GlobalConfiguration.lengthCheckTwo,
          "0",
        ),
        meridiem,
      },
    });
    setIsTimeConfirmed(true);
  }, [initialSnoozedUntil]);

  return (
    <div
      className="flex w-full justify-center items-center m-auto top-1/2 left-1/2 fixed z-z-index-modal px-padding-md py-padding-sm"
      style={{
        transform: "translate(-50%, -50%)",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className={`
    h-fit w-full 
    Qmd:w-small-popup-width 
    flex flex-col 
    px-padding-md py-padding-md 
    bg-primary-card 
    overflow-visible
    rounded-border-radius-md
    ${animate ? "Qmd:animate-slideInFromLeftSmallPopUp" : "Qmd:animate-slideInFromRightSmallPopUp"}
  `}
      >
        <div className="">
          <div className=" flex flex-col gap-gap-xs">
            <div className="flex gap-gap-sm">
              <p className={`text-text-primary  text-font-md font-semibold`}>
                {toSentenceCase(headerTitle)}
              </p>
            </div>
            <p className={`text-font-sm text-text-secondary`}>
              {toSentenceCase(secondHeaderTitle)}
            </p>
          </div>
          <div className="pt-padding-md">
            <div className="w-full">
              <div
                className="flex flex-col relative w-full open cursor-pointer opacity-base-opacity"
                onClick={() => {
                  if (dropdown) {
                    setDropdown(false);
                  } else {
                    clearCustomSnooze();
                    setIsCustomSnoozeOpen(false);

                    setCalendarCloseSignal(
                      (prev) => prev + GlobalConfiguration.lengthCheckOne,
                    );

                    setDropdown(true);
                  }
                }}
              >
                {/* Header */}
                <div
                  className={`bg-primary-act ${
                    !dropdown && "global-hover-white"
                  } z-z-index-base relative flex items-center justify-between w-full px-padding-md py-padding-sm gap-gap-sm dropdown-header ${
                    dropdown
                      ? "dropdown_border_bottom_radius_none shadow-box-shadow-right"
                      : "dropdown_border-radius-md"
                  }`}
                >
                  <span
                    className="flex-1 overflow-hidden sentence-case"
                    style={{
                      color:
                        selectedSnoozeOption === ""
                          ? "var(--text-secondary)"
                          : "var(--text-primary)",
                    }}
                  >
                    {selectedSnoozeOption === ""
                      ? placeholder
                      : selectedSnoozeOption}
                  </span>

                  <div className="flex gap-gap-sm items-center flex-shrink-0">
                    {/* Clear button */}
                    <img
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedSnoozeOption("");
                      }}
                      src={ICONS?.CLOSE}
                      alt="clear"
                      className="h-icon-xs w-icon-xs"
                      style={{
                        display: selectedSnoozeOption ? "flex" : "none",
                      }}
                    />
                    <div
                      style={{
                        display: selectedSnoozeOption ? "flex" : "none",
                      }}
                      className="border-l max-h-dropdown-divider-height h-screen items-center border-primary-bg"
                    />
                    {/* Arrow */}
                    <div
                      className={`transition-transform duration-animation-fast ease-in-out ${
                        dropdown ? "rotate-half" : "rotate-base"
                      }`}
                    >
                      <SVGComponent
                        url={ICONS?.ARROW_DOWN_WHITE}
                        color="var(--text-primary)"
                        width="var(--icon-xs)"
                        height="var(--icon-xs)"
                      />
                    </div>
                  </div>
                </div>

                {/* Dropdown panel */}
                <div
                  className="bg-primary-act z-z-index-dropdown overflow-hidden w-width-full rounded-b-border-radius-md shadow-box-shadow-right-bottom"
                  style={{
                    maxHeight: dropdown
                      ? "var(--max-animation-height-small-ddl)"
                      : "0px",
                    transition: "max-height var(--animation-fast) ease-in-out",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div
                    className="border-primary-bg border-t mx-margin-sm transition-all duration-[var(--animation-fast)]"
                    style={{
                      opacity: !dropdown
                        ? "var(--opacity-none)"
                        : "var(--base-opacity)",
                    }}
                  />

                  <div className="flex flex-col pt-padding-sm pl-padding-sm pb-padding-base max-h-max-overflow-height-small-ddl-options bg-primary-act w-full rounded-b-border-radius-md overflow-auto border-t-border-base cursor-pointer gap-gap-sm">
                    <div className="flex flex-col pr-padding-sm bg-primary-act w-full overflow-auto gap-gap-sm mb-margin-sm">
                      {QUICK_SNOOZE_OPTIONS?.map((val) => {
                        return (
                          <div
                            key={val}
                            className={`relative max-w-full gap-gap-sm z-z-index-base global-hover-white cursor-pointer py-padding-sm px-padding-sm truncate flex justify-between items-center rounded-border-radius-md flex-shrink-0 ${
                              selectedSnoozeOption === val
                                ? "bg-primary-nav-act"
                                : ""
                            }`}
                            onClick={() => {
                              clearCustomSnooze();
                              setSelectedSnoozeOption(val);
                            }}
                          >
                            <p
                              className="truncate sentence-case flex-1"
                              style={{ color: "var(--text-primary)" }}
                            >
                              {val}
                            </p>

                            <img
                              className="w-icon-md h-icon-md"
                              src={
                                selectedSnoozeOption === val
                                  ? ICONS.RADIO_ON_AMOUNT
                                  : ICONS.RADIO_OFF_AMOUNT
                              }
                              alt="radio"
                              onClick={(e) => {
                                e.stopPropagation();
                                clearCustomSnooze();
                                setSelectedSnoozeOption(val);
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="pt-padding-md text-text-secondary text-font-xs font-normal flex justify-center">
            <p>OR</p>
          </div>
          <div className="w-full pt-padding-md relative">
            <div
              className={`flex flex-col relative w-full ${
                isCustomSnoozeOpen ? "open" : ""
              } cursor-pointer`}
              onClick={() => {
                if (isCustomSnoozeOpen) {
                  setIsCustomSnoozeOpen(false);
                } else {
                  setSelectedSnoozeOption("");
                  setDropdown(false);
                  setIsCustomSnoozeOpen(true);
                }
              }}
            >
              {/* Dropdown Header */}
              <div
                className={`bg-primary-act z-z-index-base relative flex items-center justify-between w-full px-padding-md py-padding-sm gap-gap-sm dropdown-header ${
                  isCustomSnoozeOpen
                    ? "dropdown_border_bottom_radius_none shadow-box-shadow-right"
                    : "dropdown_border-radius-md"
                }`}
              >
                <span
                  className="flex-1 text-font-md truncate select-none"
                  style={{
                    color: isCustomSnoozeSelected
                      ? "var(--text-primary)"
                      : "var(--text-secondary)",
                  }}
                >
                  {isCustomSnoozeSelected
                    ? `${startDate.getDate()} ${startDate.toLocaleString(
                        "en-US",
                        {
                          month: "short",
                        },
                      )}, ${startDate.getFullYear()} - ${
                        timeData.snoozeTime.hours
                      }:${timeData.snoozeTime.minutes} ${timeData.snoozeTime.meridiem}`
                    : "Custom date and time"}
                </span>

                {/* Clear + Arrow */}
                <div className="flex gap-gap-sm items-center flex-shrink-0">
                  {/* Clear button */}
                  {isCustomSnoozeSelected && (
                    <>
                      <img
                        onClick={(e) => {
                          e.stopPropagation();
                          clearCustomSnooze();
                        }}
                        src={ICONS?.CLOSE}
                        alt="clear"
                        className="h-icon-xs w-icon-xs"
                      />
                      <div className="border-l max-h-dropdown-divider-height h-screen items-center border-primary-bg" />
                    </>
                  )}

                  {/* Arrow */}
                  <div className="transition-transform duration-animation-fast ease-in-out">
                    <SVGComponent
                      url={
                        isCustomSnoozeOpen
                          ? ICONS?.ARROW_UP_WHITE
                          : ICONS?.ARROW_DOWN_WHITE
                      }
                      color="var(--text-primary)"
                      height="var(--icon-xs)"
                      width="var(--icon-xs)"
                      alt="Arrow"
                    />
                  </div>
                </div>
              </div>

              {/* Dropdown Content */}
              <div
                className="bg-primary-act z-z-index-dropdown overflow-hidden w-width-full rounded-b-border-radius-md shadow-box-shadow-right-bottom"
                style={{
                  maxHeight: isCustomSnoozeOpen ? "720px" : "0px",
                  transition: "max-height var(--animation-fast) ease-in-out",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="border-primary-bg border-t mx-margin-sm"></div>

                <div className="flex flex-col gap-gap-md p-padding-md bg-primary-act">
                  <div className="flex flex-col gap-gap-xs">
                    <CommonCalendar
                      startDate={startDate}
                      handleDateChange={handleDateChange}
                      selectionType="single"
                      type="big"
                      minDate={new Date()}
                      isFullWidthRequired
                      expandWithinContainer
                      externalCloseSignal={calendarCloseSignal}
                    />
                  </div>

                  <div className="flex flex-col gap-gap-xs">
                    <CommonTimePicker
                      value={
                        timeData?.snoozeTime?.hours &&
                        timeData?.snoozeTime?.minutes
                          ? `${timeData.snoozeTime.hours}:${timeData.snoozeTime.minutes} ${timeData.snoozeTime.meridiem}`
                          : ""
                      }
                      handleTimeConfirm={handleTimeConfirm}
                      handleClearTime={() => {
                        setTimeData({
                          snoozeTime: {
                            hours: "",
                            minutes: "",
                            meridiem: "AM",
                          },
                        });
                        setIsTimeConfirmed(false);
                      }}
                      data_type="snooze"
                      fieldname="reminder_time"
                      keyTime="snoozeTime"
                      timeData={timeData}
                      setTimeData={handleTimeDataChange}
                      isOverlay={true}
                      onExpandChange={(isExpanded) => {
                        if (isExpanded) {
                          setCalendarCloseSignal(
                            (prev) => prev + GlobalConfiguration.lengthCheckOne,
                          );
                        }
                      }}
                    />

                    {showInvalidSnoozeTimeError && (
                      <p className="text-font-xs pt-padding-xs text-failed">
                        Invalid time
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="w-full gap-gap-sm flex justify-end pt-padding-md">
            <PrimaryButton
              btnText="Cancel"
              btnType="button"
              handleFunction={() => {
                setAnimate(true);
                setTimeout(() => {
                  btnOneHandleFunction();
                }, IntervalConfiguration.intervalSemiMid);
              }}
              enabledBgColor="transparent"
              enabledTextColor="var(--text-primary)"
              outLineColor="var(--text-primary)"
              hoverOption="white"
            />
            <PrimaryButton
              btnText="Confirm"
              handleFunction={() => {
                if (Boolean(isConfirmDisabled)) return;
                handleSnoozeClick();
              }}
              enabledBgColor="var(--positive-act)"
              enabledTextColor="var(--text-primary)"
              hoverOption="black"
              isDisabled={Boolean(isConfirmDisabled)}
              btnType="button"
              disabledTextColor="var(--text-primary)"
              disabledBgColor="var(--text-disabled)"
              inActiveVariantNeeded
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SnoozePopup;
